import { useEffect, useMemo, useRef, useState } from "react";
import QR from "./../../../assets/img/qr-code-img.png";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import { io, Socket } from "socket.io-client";
import { MOSIDA_API_ORIGIN } from "../../../config/apiUrls";
import { resolvePostOnsitePaymentSuccessRoute } from "../../../services/stylePromptResolver";
import type { AiSelfieArt } from "../../../services/aiSelfieArtService";
import PaymentSuccess from "../../../modal/PaymentSuccess";
import QrSessionExpiredModal from "../../../modal/QrSessionExpiredModal";
import {
    isPaymentSocketSuccess,
    readPaymentSession,
    savePendingPaymentOrderFromSocket,
} from "../../../utils/pendingPaymentOrder";
import { toast } from "react-toastify";
import { loadKioskTimeout } from "../../../redux/slices/kioskSlice";
import { DEFAULT_QR_EXPIRY_MS } from "../../../services/kioskTimeoutService";
import StationThemeLayout from "../../../layouts/StationThemeLayout";
import { formatPrice } from "../../../constants";
import {
    enrichStationWithThemeBackgrounds,
    persistStationSelection,
    readActiveStoredStation,
    readSelectedAiSelfieArtFromLocalStorage,
    resolveStationRouteSlug,
    type StoredStationRecord,
} from "../../../utils/stationFlowStorage";

const SECRET_KEY = process.env.REACT_APP_PAYMENT_SECRET_KEY || "";
const BASE_URL = MOSIDA_API_ORIGIN;

/** When QR pay was opened from review-cart / pack selection, success returns to the pre–print-flow path. */
const REVIEW_CART_ROUTE = "/review-cart";
const PRINT_FRAME_ROUTE = "/print-frame-selection";
const QUICK_PRINT_ROUTE = "/quick-print-frame-selection";

const QRScanToPay = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const location = useLocation();
    const { slug } = useParams();
    const {
        stationData,
        paymentType,
        previousRoute,
        selectedArt,
        totalAmount,
        snapshot,
    } = (location.state || {}) as {
        stationData?: StoredStationRecord;
        paymentType?: "onsite" | "offsite";
        previousRoute?: string;
        selectedArt?: Partial<AiSelfieArt> | null;
        totalAmount?: number;
        snapshot?: string | null;
    };
    const themeStation = useMemo(
        () => enrichStationWithThemeBackgrounds(stationData || readActiveStoredStation(), selectedArt),
        [selectedArt, stationData]
    );
    const stationSlug = resolveStationRouteSlug(themeStation, slug);
    const backTarget = previousRoute || `/${stationSlug}/print-frame-selection`;
    const paymentQR = useSelector((state: RootState) => state.qrScan?.paymentQR);
    const cartItems = useSelector((state: RootState) => state.cart?.items || []);
    const tempDataIdFromStore = useSelector((state: RootState) => state.qrScan?.tempDataId);
    const { qrExpiryMs, loaded: kioskSettingsLoaded, loading: kioskSettingsLoading } = useSelector(
        (state: RootState) => state.kiosk
    );
    const qrExpiryDurationMs = qrExpiryMs > 0 ? qrExpiryMs : DEFAULT_QR_EXPIRY_MS;
    const tempDataId = tempDataIdFromStore || readPaymentSession().tempDataId || null;
    const socketRef = useRef<Socket | null>(null);
    const postPaymentRouteRef = useRef<string | null>(null);
    const paymentHandledRef = useRef(false);
    const qrExpiredHandledRef = useRef(false);
    const navigateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const navigationContextRef = useRef({
        stationData: themeStation,
        selectedArt,
        paymentType,
        previousRoute,
        stationSlug,
    });
    const navigateRef = useRef(navigate);
    const [qrDisplaySrc, setQrDisplaySrc] = useState<string | null>(null);
    const [qrImageLoaded, setQrImageLoaded] = useState(false);
    const [showPaymentSuccessModal, setShowPaymentSuccessModal] = useState(false);
    const [showQrExpiredModal, setShowQrExpiredModal] = useState(false);
    const [qrExpiryRemainingMs, setQrExpiryRemainingMs] = useState<number | null>(null);
    const [qrExpiryEndsAt, setQrExpiryEndsAt] = useState<number | null>(null);
    const qrExpiryStartedRef = useRef(false);

    const formatQrExpiryCountdown = (ms: number) => {
        const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
    };

    const formatQrExpiryClock = (timestamp: number) => {
        const date = new Date(timestamp);
        if (Number.isNaN(date.getTime())) return "";
        return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
    };

    const displayTotal = useMemo(() => {
        if (typeof totalAmount === "number" && !Number.isNaN(totalAmount)) {
            return totalAmount;
        }
        return (cartItems || []).reduce((sum, item) => {
            const price = typeof item.price === "number" ? item.price : parseFloat(String(item.price || "0"));
            return sum + price * (item.quantity || 0);
        }, 0);
    }, [cartItems, totalAmount]);

    navigationContextRef.current = {
        stationData: themeStation,
        selectedArt,
        paymentType,
        previousRoute,
        stationSlug,
    };
    navigateRef.current = navigate;

    useEffect(() => {
        if (kioskSettingsLoaded || kioskSettingsLoading) return;
        dispatch(loadKioskTimeout());
    }, [dispatch, kioskSettingsLoaded, kioskSettingsLoading]);

    const handlePaymentSuccessModalHide = () => {
        setShowPaymentSuccessModal(false);
        const route = postPaymentRouteRef.current;
        if (route) {
            navigate(route);
        }
    };

    const navigateBackFromQrPay = () => {
        const backState = {
            stationData,
            selectedArt,
            snapshot: snapshot || undefined,
        };
        navigate(backTarget, { state: backState });
    };

    const handleQrExpiredModalHide = () => {
        setShowQrExpiredModal(false);
        navigateBackFromQrPay();
    };

    const clearNavigateTimer = () => {
        if (navigateTimerRef.current) {
            clearTimeout(navigateTimerRef.current);
            navigateTimerRef.current = null;
        }
    };

    useEffect(() => {
        if (!paymentQR) {
            setQrDisplaySrc(null);
            setQrImageLoaded(false);
            return;
        }

        let cancelled = false;
        setQrDisplaySrc(null);
        setQrImageLoaded(false);

        const loadImage = (src: string, isFallback = false) => {
            const img = new Image();
            img.onload = () => {
                if (cancelled) return;
                setQrDisplaySrc(src);
                setQrImageLoaded(true);
            };
            img.onerror = () => {
                if (cancelled) return;
                if (!isFallback) {
                    loadImage(QR, true);
                    return;
                }
                setQrDisplaySrc(QR);
                setQrImageLoaded(true);
            };
            img.src = src;
        };

        loadImage(paymentQR);

        return () => {
            cancelled = true;
        };
    }, [paymentQR]);

    useEffect(() => {
        if (!paymentQR || !qrImageLoaded) {
            qrExpiryStartedRef.current = false;
            setQrExpiryEndsAt(null);
            setQrExpiryRemainingMs(null);
            return;
        }

        if (qrExpiryStartedRef.current) return;

        const endsAt = Date.now() + qrExpiryDurationMs;
        qrExpiryStartedRef.current = true;
        setQrExpiryEndsAt(endsAt);

        const tick = () => {
            const remaining = endsAt - Date.now();
            setQrExpiryRemainingMs(remaining > 0 ? remaining : 0);
        };

        tick();
        const intervalId = window.setInterval(tick, 1000);
        return () => window.clearInterval(intervalId);
    }, [paymentQR, qrImageLoaded, qrExpiryDurationMs]);

    useEffect(() => {
        if (qrExpiryRemainingMs !== 0 || !qrExpiryEndsAt) return;
        if (paymentHandledRef.current || qrExpiredHandledRef.current) return;

        qrExpiredHandledRef.current = true;
        clearNavigateTimer();

        if (socketRef.current) {
            socketRef.current.off("connect");
            socketRef.current.off("paymentStatusUpdate");
            socketRef.current.off("disconnect");
            socketRef.current.off("connect_error");
            if (tempDataId) {
                socketRef.current.off(tempDataId);
            }
            socketRef.current.disconnect();
            socketRef.current = null;
        }

        setShowQrExpiredModal(true);
    }, [qrExpiryRemainingMs, qrExpiryEndsAt, tempDataId]);

    useEffect(() => {
        if (!tempDataId) {
            console.error("tempDataId is missing");
            return;
        }

        paymentHandledRef.current = false;

        const handlePaymentUpdate = (data: any) => {
            if (paymentHandledRef.current) return;

            if (!isPaymentSocketSuccess(data)) {
                if (data && typeof data === "object") {
                    const message =
                        (data as { message?: string }).message ||
                        ((data as { data?: { message?: string } }).data?.message);
                    if (message) {
                        toast.error(message);
                    }
                }
                return;
            }

            paymentHandledRef.current = true;
            const ctx = navigationContextRef.current;
            const payload = typeof data === "object" && data !== null ? data : {};

            // Store orderId + orderNumber(s) from payment socket success payload.
            const paymentSession = savePendingPaymentOrderFromSocket(payload, tempDataId);
            const orderNumbers =
                paymentSession.orderNumbers && paymentSession.orderNumbers.length > 0
                    ? paymentSession.orderNumbers
                    : paymentSession.orderNumber
                        ? [paymentSession.orderNumber]
                        : [];

            if (ctx.previousRoute === QUICK_PRINT_ROUTE) {
                const route = resolvePostOnsitePaymentSuccessRoute();
                postPaymentRouteRef.current = route;
                setShowPaymentSuccessModal(true);
                clearNavigateTimer();
                navigateTimerRef.current = setTimeout(() => {
                    setShowPaymentSuccessModal(false);
                    navigateRef.current(route, {
                        state: { orderNumbers, orderNumber: paymentSession.orderNumber },
                    });
                }, 3000);
                return;
            }

            clearNavigateTimer();
            navigateTimerRef.current = setTimeout(() => {
                const paymentArt = ctx.selectedArt || readSelectedAiSelfieArtFromLocalStorage();
                const themedStation = enrichStationWithThemeBackgrounds(
                    ctx.stationData,
                    paymentArt
                );
                persistStationSelection(themedStation);
                const nextState = {
                    stationData: themedStation,
                    selectedArt: paymentArt,
                    orderNumber: paymentSession.orderNumber,
                    orderNumbers,
                    paymentType:
                        ctx.paymentType ||
                        (ctx.previousRoute === REVIEW_CART_ROUTE ||
                        ctx.previousRoute === PRINT_FRAME_ROUTE
                            ? "offsite"
                            : undefined),
                };
                navigateRef.current(`/${ctx.stationSlug || resolveStationRouteSlug(themedStation)}/payment-success`, {
                    state: nextState,
                });
            }, 2500);
        };

        let socket: Socket | null = null;

        try {
            socket = io(`${BASE_URL}/ws/payment`, {
                query: { secretKey: SECRET_KEY },
                transports: ["websocket"],
                reconnection: true,
                reconnectionDelay: 1000,
                reconnectionDelayMax: 5000,
                reconnectionAttempts: 5,
                timeout: 20000,
                forceNew: true,
                upgrade: false,
                auth: {
                    secretKey: SECRET_KEY,
                },
            });

            socketRef.current = socket;

            socket.on("connect", () => {
                console.log("Payment socket connected:", socket?.id);
                socket?.emit("registerUser", { userId: tempDataId });
            });

            socket.on(tempDataId, (data: any) => {
                console.log("Payment status update received (by ID):", data);
                handlePaymentUpdate(data);
            });

            socket.on("paymentStatusUpdate", (data: any) => {
                console.log("Payment status update received (generic):", data);
                if (data && (data.temp_data_id === tempDataId || data.id === tempDataId)) {
                    handlePaymentUpdate(data);
                }
            });

            socket.on("disconnect", () => {
                console.log("Payment socket disconnected");
            });

            socket.on("connect_error", (error) => {
                console.error("Payment socket connection error:", error);
            });
        } catch (e) {
            console.error("Socket setup error:", e);
        }

        return () => {
            clearNavigateTimer();
            if (socketRef.current) {
                socketRef.current.off("connect");
                socketRef.current.off(tempDataId);
                socketRef.current.off("paymentStatusUpdate");
                socketRef.current.off("disconnect");
                socketRef.current.off("connect_error");
                socketRef.current.disconnect();
                socketRef.current = null;
            }
        };
    }, [tempDataId]);



    return (
        <StationThemeLayout
            className="reviewCartSection qrScanToPayPage"
            station={themeStation}
            showStationLogo
            onBack={navigateBackFromQrPay}
        >
            <div className="qrScanToPayInner">
                <div className="qrCodePaySection">
                    <div className="qrCard shadow py-md-5 py-4">
                        <div className="heading">Scan QR to Pay</div>
                        <div className="description">Use your phone to scan the QR</div>

                        <div className="qrCodeImageSection ">
                            <div className="qrCodeImageWrap">
                                {qrImageLoaded && qrDisplaySrc ? (
                                    <img src={qrDisplaySrc} alt="Payment QR Code" />
                                ) : (
                                    <div className="qrCodeShimmer" aria-hidden="true" />
                                )}
                            </div>
                        </div>

                        {qrImageLoaded && (
                            <>
                            <div className="qrPayTotalAmount">
                              {formatPrice(displayTotal)} USD
                            </div>
                            <span> Amount Payable</span>
                            </>
                        )}

                        {qrImageLoaded && qrExpiryEndsAt && (
                            <div
                                className={`qrExpiryTile mt-4`}
                                aria-live="polite"
                            >
                                <div className="qrExpiryTile__label">
                                    {qrExpiryRemainingMs !== null && qrExpiryRemainingMs > 0 &&
                                        <>
                                            <span>Expires in</span>
                                            <span className="ms-2" style={{ fontSize: '18px' }}>
                                                {formatQrExpiryCountdown(qrExpiryRemainingMs)}
                                            </span>
                                        </>
                                    }
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {
                showPaymentSuccessModal && (
                    <PaymentSuccess show={showPaymentSuccessModal} onHide={handlePaymentSuccessModalHide} />
                )
            }
            {
                showQrExpiredModal && (
                    <QrSessionExpiredModal show={showQrExpiredModal} onHide={handleQrExpiredModalHide} title="This payment QR has expired. Please try again." />
                )
            }
        </StationThemeLayout>
    );
};

export default QRScanToPay;
